import React, { useCallback, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-final-form';
import { useQuery } from '@apollo/client';
import useSearchModal from '../../../../hooks/useSearchModal';
import {
  hidePopupHeader,
  showPopupHeader,
  setChosenLocationNames,
  setLocationIds,
  setCurrentState
} from '../../../../actions';
import { Input, InputTypeMap } from '../../../ui/Input';
import { Modal } from '../../../ui/Modal';
import { NewSearchPopupLocationsProps } from './types';
import { selectSearchFormLocationResetButtonIsDisabled } from '../../../../store/selectors';
import { LocationsContainer, modalFooterStyles } from './styled';
import { NewSearchLocation } from '../NewSearchLocation';
import SelectLocation from '../../../ItemCreationForm/SelectLocation';
import { NewSearchLocationBar } from '../NewSearchLocationBar';
import { locationGroupsQuery } from '../../../../queries';
import Loader from '../../../Loader';
import { Location, LocationGroup } from '../types';
import { getChosenLocationNames } from '../../../../utils/helpers/getChosenLocationNames';

const listTypesMap = {
  search: NewSearchLocation,
  create: SelectLocation
};

export const NewSearchPopupLocations = ({
  undo,
  isOpen,
  listType,
  popupTitle,
  closeHandler,
  inputPlaceholder,
  cityId,
  openCitiesHandler,
  setLocationsIsLoading,
  hasLocations,
  modalParams,
  renderSubmitText,
  setInitCoordinates
}: NewSearchPopupLocationsProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { submit, getState, change } = useForm();
  const inputEl = useRef<HTMLInputElement | null>(null);

  const locationsResetButtonIsDisabled = useSelector(
    selectSearchFormLocationResetButtonIsDisabled
  );
  const currentLocationIds = getState().values.location_ids || [];

  const [chosenLocationIds, setChosenLocationIds] =
    useState<string[]>(currentLocationIds);
  const [locations, setLocations] = useState<Location[]>([]);
  const [currentTabId, setCurrentTabId] = useState(0);
  const [townshipsCount, setTownshipsCount] = useState(0);

  const isCreate = listType === 'create';
  const initialScreen = modalParams?.initialScreen;

  const locationGroupsQueryVariables = {
    ids: [1, 2, 3, 4],
    cityId
  };
  const { data, loading } = useQuery<{
    locationGroups: LocationGroup[];
  }>(locationGroupsQuery(locationGroupsQueryVariables), {
    variables: locationGroupsQueryVariables
  });

  const {
    isFocused,
    searchString,
    isInputOnFocus,
    onBlurHandler,
    onFocusHandler,
    onResetHandler,
    onChangeHandler,
    setIsSubmittedForm,
    defaultCancelHandler
  } = useSearchModal(inputEl);

  const handleReset = () => {
    dispatch(setLocationIds([]));
    dispatch(setChosenLocationNames([]));
    defaultCancelHandler();
  };

  const cancelHandler = () => {
    dispatch(showPopupHeader());
    defaultCancelHandler();
  };

  const handleCloseWithUndo = useCallback(() => {
    undo();
    closeHandler();
  }, [undo, closeHandler]);

  const closePopup = () => {
    dispatch(showPopupHeader());
    closeHandler();
    cancelHandler();
  };

  const onConfirm = () => {
    change('location_ids', chosenLocationIds);
    dispatch(setLocationIds(chosenLocationIds));
    dispatch(
      setCurrentState({
        ...getState().values,
        location_ids: chosenLocationIds
      })
    );
    dispatch(
      setChosenLocationNames(
        getChosenLocationNames(locations, chosenLocationIds)
      )
    );
    if (initialScreen === 'locations' || initialScreen === 'city') {
      return submit().then(closePopup);
    }
    closePopup();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.keyCode === 13 && e.shiftKey === false) {
      e.preventDefault();
      setIsSubmittedForm(true);
    }
  };

  const onFocusInputHandler = () => {
    dispatch(hidePopupHeader());
    onFocusHandler();
  };

  const ListComponent = listTypesMap[listType];

  const { locationGroups = [] } = data || {};
  const nonEmptyLocationGroups = locationGroups.filter(
    group => !!group.locations.length
  );
  const confirmText = renderSubmitText
    ? renderSubmitText({ locationIds: chosenLocationIds })
    : t('search.city_search.ready');

  const submitButtonText =
    initialScreen === 'filters'
      ? t('search.buttons.add_to_search')
      : confirmText;

  const handleResetButton = () => {
    onResetHandler();
    if (isCreate) {
      onBlurHandler();
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      modalId="locations"
      onClose={handleCloseWithUndo}
      cypress="new-search-popup--locations"
      title={popupTitle}
      resetIsDisabled={locationsResetButtonIsDisabled && !searchString}
      onReset={isCreate ? undefined : handleReset}
      resetText={t('common.reset')}
      confirmText={submitButtonText}
      onConfirm={onConfirm}
      showHeaderBorder={false}
      footerStyles={modalFooterStyles}
    >
      <LocationsContainer $loading={loading}>
        <Loader loading={loading}>
          <NewSearchLocationBar
            isCreate={isCreate}
            openCitiesHandler={openCitiesHandler}
            cityId={cityId}
            setLocationsIsLoading={setLocationsIsLoading}
            searchString={searchString}
            chosenLocationIds={chosenLocationIds}
            locationGroups={nonEmptyLocationGroups}
            townshipsCount={townshipsCount}
            currentTabId={currentTabId}
            setCurrentTabId={setCurrentTabId}
          >
            {inputPlaceholder && (
              <Input
                refEl={inputEl}
                value={searchString}
                onKeyDown={handleKeyDown}
                type={InputTypeMap.search}
                onBlurHandler={onBlurHandler}
                placeholder={inputPlaceholder}
                stat="new-search-find-locations"
                onResetHandler={handleResetButton}
                onChangeHandler={onChangeHandler}
                onFocusHandler={onFocusInputHandler}
                isShowReset={!!searchString}
                isInsideModalWithAnimation
              />
            )}
          </NewSearchLocationBar>
          <ListComponent
            noLocations={!hasLocations}
            closePopupHandler={closeHandler}
            locationGroups={nonEmptyLocationGroups}
            isFocused={isCreate ? isInputOnFocus : isFocused}
            {...{
              searchString,
              cancelHandler,
              setInitCoordinates,
              setChosenLocationIds,
              chosenLocationIds,
              openCitiesHandler,
              currentTabId,
              setTownshipsCount,
              setLocations
            }}
          />
        </Loader>
      </LocationsContainer>
    </Modal>
  );
};
